'use client'

import {
  PageSectionsContentSectionContentBlocksFaqs,
  PageSectionsContentSectionContentBlocksFaqsFaq,
} from '@/tina/__generated__/types'
import * as stylex from '@stylexjs/stylex'
import { ForwardedRef, MouseEvent, useRef, useState } from 'react'
import { tinaField } from 'tinacms/dist/react'
import { a11y } from '../styles/a11y'
import { layout } from '../styles/layout'
import { color, spacing } from '../styles/tokens.stylex'
import { typography } from '../styles/typography'
import { Button } from './Buttons'
import { RichText } from './RichText'
import { sendGTMEvent } from '@next/third-parties/google'

export interface FaqsProps {
  data?: PageSectionsContentSectionContentBlocksFaqs
}
interface FaqProps {
  thisFaqIsOpen: boolean
  toggleActive: (event?: MouseEvent<HTMLButtonElement>) => void
  data: PageSectionsContentSectionContentBlocksFaqsFaq
}

export function Faqs({ data }: FaqsProps) {
  const [activeFaq, setActiveFaq] = useState<string | null>()
  return (
    <div {...stylex.props([sx.root])}>
      {data?.faq.map((faq, i) => {
        const id = i + '-' + faq.question.replace(' ', '')
        const clickHandler = () => {
          const isActive = activeFaq === id
          if (!isActive) {
            setActiveFaq(id)
            sendGTMEvent({
              event: 'faq_open',
              faq: faq.question,
            })
          } else {
            setActiveFaq(null)
            sendGTMEvent({
              event: 'faq_close',
              faq: faq.question,
            })
          }
        }
        return (
          <Faq
            key={id}
            thisFaqIsOpen={activeFaq === id}
            toggleActive={clickHandler}
            data={faq}
          />
        )
      })}
    </div>
  )
}

function Faq({ data, thisFaqIsOpen, toggleActive, ...props }: FaqProps) {
  const buttonRef = useRef<HTMLButtonElement>(null)
  const questionClickHandler = () => buttonRef?.current?.click()
  return data ? (
    <article
      {...stylex.props(
        layout.contentCard,
        sx.faqBackground,
        thisFaqIsOpen && sx.faqBackgroundActive
      )}
      onClick={questionClickHandler}
      {...props}
    >
      <p
        {...stylex.props(typography.heading4, sx.question)}
        data-tina-field={tinaField(data, 'question')}
      >
        {data.question}
      </p>
      <Button
        variant="ghost"
        theme="neutral"
        iconOnly="chevron-down"
        iconOnlyStyles={[sx.icon, thisFaqIsOpen && sx.faceUp]}
        ref={buttonRef as ForwardedRef<HTMLButtonElement>}
        onClick={toggleActive}
        styles={sx.toggleButton}
      >
        Expand FAQ
      </Button>

      <div
        {...stylex.props(sx.answer, !thisFaqIsOpen && a11y.visuallyHidden)}
        data-tina-field={tinaField(data, 'answer')}
      >
        <RichText>{data.answer}</RichText>
      </div>
    </article>
  ) : null
}

const sx = stylex.create({
  root: {
    display: 'grid',
    gap: spacing.gap_med,
    cursor: 'pointer',
  },
  toggleButton: {
    gridArea: 'toggle',
    transform: 'translateX(8px)',
  },
  question: {
    gridArea: 'question',
  },
  answer: {
    gridArea: 'answer',
    marginTop: '0.5em',
    maxInlineSize: '90ch',
    textWrap: 'pretty',
  },
  faqBackground: {
    backgroundColor: {
      default: color.surfaceBaseSecondary,
      ':hover': color.surfaceBasePrimary,
    },
    alignItems: 'center',
    display: 'grid',
    gridTemplateAreas: `
    'question toggle'
    'answer answer'
    `,
    gridTemplateColumns: `1fr fit-content(100%)`,
    gap: `0 ${spacing.gap_med}`,
  },
  faqBackgroundActive: { background: color.surfaceBasePrimary },
  icon: {
    transition: 'transform 0.2s ease-in-out',
    willChange: 'transform',
    transformOrigin: 'center',
    height: '1.5rem',
    width: '1.5rem',
    marginBottom: `-${spacing.inset_xs}`,
  },
  faceUp: { transform: 'rotate(-180deg)' },
})
